import { get } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { getHandlebars } from 'dashboard/lib/utils/handlebars';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { LayoutVariableDefinitionType } from 'dashboard/models/layout-variable-definitions';
import { useLazyResolveCaseVariableQuery } from 'dashboard/redux/api/cases';
import { toStringFromBase64WithUnicode } from 'lib/base64';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { useLazyRTKErrorHandler } from 'lib/hooks/useRTKErrorHandler';

interface UseCaseDataContextProps {
  variableDefinitions: CaseVariableDefinition[];
  caseId: string;
  isPreview: boolean;
}

const getDependencyKey = (dependency: string) => {
  return dependency.split('.')[0];
};

const computeStringDependencies = (base64EncodedString: string | null) => {
  if (!base64EncodedString) {
    return [];
  }

  const code = toStringFromBase64WithUnicode(base64EncodedString);

  return getHandlebars(code);
};

const computeVariableDefinitionDependencies = (variableDefinition: CaseVariableDefinition) => {
  const dependencies: string[] = [];

  const layoutVariableDefinition = variableDefinition.attributes['variable-definition'];
  dependencies.push(...computeStringDependencies(layoutVariableDefinition.transform_results));

  // Also include dependencies from the SQL query
  if (layoutVariableDefinition.type === LayoutVariableDefinitionType.SnowflakeQuery) {
    dependencies.push(...computeStringDependencies(layoutVariableDefinition.sql));
  }

  return dependencies;
};

export function useCaseDataContext({
  caseId,
  variableDefinitions,
  isPreview,
}: UseCaseDataContextProps) {
  const isDataContextLayerEnabled = useFeatureFlag('dashboard:cases-data-context-layer');
  const [trigger] = useLazyResolveCaseVariableQuery();
  const { handleUseRTKError } = useLazyRTKErrorHandler();
  const [variableLoadingMap, setVariableLoadingMap] = useState<Record<string, boolean>>(
    variableDefinitions.reduce(
      (acc, variableDefinition) => ({
        ...acc,
        [variableDefinition.attributes.key]: true,
      }),
      {},
    ),
  );
  const isLoading = Object.values(variableLoadingMap).some((loading) => loading);
  const [dataContext, setDataContext] = useState<Record<string, any>>(
    variableDefinitions.reduce(
      (acc, variableDefinition) => ({
        ...acc,
        [variableDefinition.attributes.key]: null,
      }),
      {},
    ),
  );

  useEffect(() => {
    setDataContext(
      variableDefinitions.reduce(
        (acc, variableDefinition) => ({
          ...acc,
          [variableDefinition.attributes.key]: null,
        }),
        {},
      ),
    );
    setVariableLoadingMap(
      variableDefinitions.reduce(
        (acc, variableDefinition) => ({
          ...acc,
          [variableDefinition.attributes.key]: true,
        }),
        {},
      ),
    );
  }, [variableDefinitions, caseId]);

  const variableDependencyMap = useMemo(() => {
    const map: Record<string, string[]> = {};

    variableDefinitions.forEach((variableDefinition) => {
      map[variableDefinition.attributes.key] =
        computeVariableDefinitionDependencies(variableDefinition);
    });

    return map;
  }, [variableDefinitions]);

  const resolveVariable = async (
    caseId: string,
    variableDefinition: CaseVariableDefinition,
    variableMap: Record<string, any>,
  ) => {
    const response = await trigger({
      caseToken: caseId,
      variableDefinitionToken: variableDefinition.id,
      variableMap,
    });

    if (response.error) {
      handleUseRTKError({
        error: response.error,
        onError: isPreview
          ? undefined
          : () => ({
              message:
                'An unexpected error occurred while loading the case. Some data may be unavailable.',
              level: MessageType.Notice,
            }),
      });
      throw new Error('Error resolving variable');
    } else {
      return response.data;
    }
  };

  const resolveVariableDefinitions = async () => {
    variableDefinitions.forEach(async (variableDefinition) => {
      const dependencies = variableDependencyMap[variableDefinition.attributes.key];
      const hasDependenciesLoading = dependencies.some(
        (dep) => variableLoadingMap[getDependencyKey(dep)],
      );

      const variableMap = dependencies.reduce<Record<string, any>>((depMap, dep) => {
        depMap[dep] = get(dep, dataContext);
        return depMap;
      }, {});

      if (
        !hasDependenciesLoading &&
        variableLoadingMap[variableDefinition.attributes.key] &&
        caseId
      ) {
        resolveVariable(caseId, variableDefinition, variableMap).then((result) => {
          setDataContext((prev) => ({
            ...prev,
            [variableDefinition.attributes.key]: result,
          }));

          setVariableLoadingMap((prev) => ({
            ...prev,
            [variableDefinition.attributes.key]: false,
          }));
        });
      }
    });
  };

  useEffect(() => {
    if (isDataContextLayerEnabled) {
      resolveVariableDefinitions();
    }
  }, [isDataContextLayerEnabled, caseId, variableLoadingMap, dataContext]);

  return { dataContext, isLoading };
}
